import { ref } from 'vue'

/* ============================================================================
 * 皮肤/主题配置系统
 * ============================================================================
 *
 * 功能说明：
 *   1. 每个皮肤定义一组完整的颜色方案，包含背景色、文字色、强调色等
 *   2. 支持深色系和浅色系皮肤
 *   3. 用户选择自动保存到 localStorage
 *   4. 通过 CSS 变量注入，全局生效
 *
 * 添加新皮肤的步骤：
 *   1. 在 presets 对象中新增一个 key
 *   2. 填写 name（中英文名称）、type（dark/light）、colors（完整颜色方案）
 *   3. 皮肤会立即出现在导航栏的切换器中
 *
 * 修改已有皮肤的颜色：
 *   直接在对应皮肤的 colors 对象中修改值即可，所有引用了 CSS 变量的元素会自动更新
 * ============================================================================
 */

// ---------------------------------------------------------------------------
// 工具函数
// ---------------------------------------------------------------------------

/**
 * 将十六进制颜色字符串（如 #14608B）转换为 RGB 分量字符串（如 "20, 96, 139"）
 * 用于 CSS 的 rgba(var(--color-bg-rgb), 0.92) 写法
 * @param {string} hex - 十六进制颜色值
 * @returns {string} RGB 分量，逗号分隔
 */
function hexToRgb(hex) {
  const r = parseInt(hex.slice(1, 3), 16)
  const g = parseInt(hex.slice(3, 5), 16)
  const b = parseInt(hex.slice(5, 7), 16)
  return `${r}, ${g}, ${b}`
}

/**
 * 对 RGB 分量进行整体平移，实现提亮/压暗效果
 * @param {string} rgbStr - RGB 分量字符串，如 "20, 96, 139"
 * @param {number} factor - 平移量，正数提亮，负数压暗
 * @returns {string} 调整后的 RGB 分量字符串
 */
function shade(rgbStr, factor) {
  const [r, g, b] = rgbStr.split(', ').map(Number)
  const clamp = (v) => Math.max(0, Math.min(255, Math.round(v + factor)))
  return `${clamp(r)}, ${clamp(g)}, ${clamp(b)}`
}

/**
 * 将 RGB 分量字符串转回十六进制颜色字符串
 * @param {string} rgb - RGB 分量字符串
 * @returns {string} 六位十六进制颜色（不含 #）
 */
function rgbToHex(rgb) {
  const [r, g, b] = rgb.split(', ').map(Number)
  return [r, g, b].map((c) => c.toString(16).padStart(2, '0')).join('')
}

// ---------------------------------------------------------------------------
// 预设皮肤定义
// ---------------------------------------------------------------------------

/**
 * 皮肤预设配置
 *
 * 每个皮肤包含：
 *   - name: 多语言显示名称
 *   - type: 'dark'(深色系) 或 'light'(浅色系)，决定文字/边框的默认方向
 *   - colors: 完整的 CSS 颜色变量值
 *     ├── bg           页面主背景色
 *     ├── bgCard       卡片/面板背景色
 *     ├── bgCardHover  卡片悬停背景色
 *     ├── footerBg     页脚背景色
 *     ├── text         主文字颜色
 *     ├── textMuted    次要文字颜色（表单标签、描述文字等）
 *     ├── heading      标题文字颜色（页脚标题、section 标题等）
 *     ├── border       通用边框颜色
 *     ├── primary      主强调色（链接、高亮按钮、hover 状态等）
 *     ├── primaryDark  深强调色（渐变终点等）
 *     ├── accent       辅助强调色
 *     ├── gradient     品牌渐变（标题文字渐变、主按钮背景）
 *     ├── navText      导航链接文字颜色
 *     ├── navHoverBg   导航链接悬停背景色
 *     ├── cardBorderHover  卡片悬停边框颜色
 *     ├── cardShadow       卡片悬停阴影
 *     └── headerShadow     导航栏滚动后的阴影
 *
 * 修改颜色时注意：
 *   - 深色系皮肤：text 应为浅色，border 为白色半透明
 *   - 浅色系皮肤：text 应为深色，border 为黑色半透明
 */
const presets = {
  // ======================== 深色系皮肤 ========================

  'tech-blue': {
    name: { zh: '科技蓝', en: 'Tech Blue' },
    type: 'dark',
    colors: {
      // 背景色系
      bg: '#14608B',
      bgCard: '#1a6e9e',
      bgCardHover: '#207daf',
      footerBg: '#0f4f73',
      // 文字色系
      text: '#e2e8f0',
      textMuted: 'rgba(255, 255, 255, 0.55)',
      heading: '#ffffff',
      // 边框
      border: 'rgba(255, 255, 255, 0.08)',
      // 强调色系
      primary: '#00d4ff',
      primaryDark: '#0066ff',
      accent: '#3b82f6',
      gradient: 'linear-gradient(135deg, #00d4ff, #0066ff)',
      // 导航
      navText: 'rgba(255, 255, 255, 0.75)',
      navHoverBg: 'rgba(0, 212, 255, 0.08)',
      // 交互效果
      cardBorderHover: 'rgba(0, 212, 255, 0.25)',
      cardShadow: '0 8px 30px rgba(0, 0, 0, 0.3)',
      headerShadow: '0 4px 30px rgba(0, 0, 0, 0.3)',
    },
  },

  'deep-space': {
    name: { zh: '深空黑', en: 'Deep Space' },
    type: 'dark',
    colors: {
      // 背景色系
      bg: '#0a0f1e',
      bgCard: '#111827',
      bgCardHover: '#1a2332',
      footerBg: '#080c18',
      // 文字色系
      text: '#e2e8f0',
      textMuted: 'rgba(255, 255, 255, 0.5)',
      heading: '#ffffff',
      // 边框
      border: 'rgba(255, 255, 255, 0.08)',
      // 强调色系
      primary: '#00d4ff',
      primaryDark: '#7c3aed',
      accent: '#3b82f6',
      gradient: 'linear-gradient(135deg, #00d4ff, #7c3aed)',
      // 导航
      navText: 'rgba(255, 255, 255, 0.7)',
      navHoverBg: 'rgba(0, 212, 255, 0.08)',
      // 交互效果
      cardBorderHover: 'rgba(0, 212, 255, 0.2)',
      cardShadow: '0 8px 30px rgba(0, 0, 0, 0.4)',
      headerShadow: '0 4px 30px rgba(0, 0, 0, 0.4)',
    },
  },

  'graphite': {
    name: { zh: '石墨灰', en: 'Graphite' },
    type: 'dark',
    colors: {
      // 背景色系 —— 沉稳的蓝灰色调，适合工业/制造行业
      bg: '#2c3e50',
      bgCard: '#34495e',
      bgCardHover: '#3d566e',
      footerBg: '#1e2d3a',
      // 文字色系
      text: '#e8ecf1',
      textMuted: 'rgba(255, 255, 255, 0.5)',
      heading: '#ffffff',
      // 边框
      border: 'rgba(255, 255, 255, 0.1)',
      // 强调色系
      primary: '#5dade2',
      primaryDark: '#2e86c1',
      accent: '#3498db',
      gradient: 'linear-gradient(135deg, #5dade2, #2e86c1)',
      // 导航
      navText: 'rgba(255, 255, 255, 0.75)',
      navHoverBg: 'rgba(93, 173, 226, 0.1)',
      // 交互效果
      cardBorderHover: 'rgba(93, 173, 226, 0.25)',
      cardShadow: '0 8px 30px rgba(0, 0, 0, 0.35)',
      headerShadow: '0 4px 30px rgba(0, 0, 0, 0.35)',
    },
  },

  'amethyst': {
    name: { zh: '暗紫', en: 'Amethyst' },
    type: 'dark',
    colors: {
      // 背景色系 —— 深邃紫调，科技感与优雅并存
      bg: '#1a1433',
      bgCard: '#231e42',
      bgCardHover: '#2d2656',
      footerBg: '#120e24',
      // 文字色系
      text: '#e0dce8',
      textMuted: 'rgba(255, 255, 255, 0.5)',
      heading: '#ffffff',
      // 边框
      border: 'rgba(255, 255, 255, 0.08)',
      // 强调色系
      primary: '#a78bfa',
      primaryDark: '#7c3aed',
      accent: '#8b5cf6',
      gradient: 'linear-gradient(135deg, #a78bfa, #7c3aed)',
      // 导航
      navText: 'rgba(255, 255, 255, 0.7)',
      navHoverBg: 'rgba(167, 139, 250, 0.1)',
      // 交互效果
      cardBorderHover: 'rgba(167, 139, 250, 0.25)',
      cardShadow: '0 8px 30px rgba(0, 0, 0, 0.4)',
      headerShadow: '0 4px 30px rgba(0, 0, 0, 0.4)',
    },
  },

  'steel-blue': {
    name: { zh: '钢蓝色', en: 'Steel Blue' },
    type: 'dark',
    colors: {
      // 背景色系 —— 金属质感蓝，契合高端制造设备企业定位
      bg: '#1e3a5f',
      bgCard: '#264a73',
      bgCardHover: '#2f5a8a',
      footerBg: '#152c48',
      // 文字色系
      text: '#dce4f0',
      textMuted: 'rgba(255, 255, 255, 0.5)',
      heading: '#ffffff',
      // 边框
      border: 'rgba(255, 255, 255, 0.08)',
      // 强调色系
      primary: '#64b5f6',
      primaryDark: '#1976d2',
      accent: '#42a5f5',
      gradient: 'linear-gradient(135deg, #64b5f6, #1976d2)',
      // 导航
      navText: 'rgba(255, 255, 255, 0.75)',
      navHoverBg: 'rgba(100, 181, 246, 0.1)',
      // 交互效果
      cardBorderHover: 'rgba(100, 181, 246, 0.25)',
      cardShadow: '0 8px 30px rgba(0, 0, 0, 0.35)',
      headerShadow: '0 4px 30px rgba(0, 0, 0, 0.35)',
    },
  },

  // ======================== 浅色系皮肤 ========================

  'light': {
    name: { zh: '明亮白', en: 'Light' },
    type: 'light',
    colors: {
      // 背景色系 —— 干净明亮的灰白底，适合长时间阅读
      bg: '#f0f4f8',
      bgCard: '#ffffff',
      bgCardHover: '#f7f9fb',
      footerBg: '#e2e8f0',
      // 文字色系 —— 深色文字，高对比度
      text: '#1e293b',
      textMuted: 'rgba(0, 0, 0, 0.5)',
      heading: '#0f172a',
      // 边框 —— 深色半透明
      border: 'rgba(0, 0, 0, 0.08)',
      // 强调色系 —— 保持品牌蓝色
      primary: '#14608B',
      primaryDark: '#0d4a6e',
      accent: '#1e7ab0',
      gradient: 'linear-gradient(135deg, #14608B, #0d4a6e)',
      // 导航
      navText: 'rgba(0, 0, 0, 0.65)',
      navHoverBg: 'rgba(20, 96, 139, 0.08)',
      // 交互效果
      cardBorderHover: 'rgba(20, 96, 139, 0.25)',
      cardShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
      headerShadow: '0 2px 20px rgba(0, 0, 0, 0.08)',
    },
  },

  // ======================== 新增高对比度皮肤 ========================

  'crimson': {
    name: { zh: '赤霞红', en: 'Crimson' },
    type: 'dark',
    colors: {
      // 背景色系 —— 深红黑底，热烈且高辨识度
      bg: '#1a0f14',
      bgCard: '#2d1520',
      bgCardHover: '#3d1e2c',
      footerBg: '#120a0e',
      // 文字色系
      text: '#f0e4e8',
      textMuted: 'rgba(255, 255, 255, 0.5)',
      heading: '#ffffff',
      // 边框
      border: 'rgba(255, 255, 255, 0.08)',
      // 强调色系 —— 珊瑚橙红系
      primary: '#ff6b6b',
      primaryDark: '#e8453c',
      accent: '#ff8c52',
      gradient: 'linear-gradient(135deg, #ff6b6b, #ff8c52)',
      // 导航
      navText: 'rgba(255, 255, 255, 0.7)',
      navHoverBg: 'rgba(255, 107, 107, 0.1)',
      // 交互效果
      cardBorderHover: 'rgba(255, 107, 107, 0.25)',
      cardShadow: '0 8px 30px rgba(0, 0, 0, 0.4)',
      headerShadow: '0 4px 30px rgba(0, 0, 0, 0.4)',
    },
  },

  'emerald': {
    name: { zh: '翡翠绿', en: 'Emerald' },
    type: 'dark',
    colors: {
      // 背景色系 —— 深森林绿底，自然科技感
      bg: '#0a1a14',
      bgCard: '#0f241d',
      bgCardHover: '#163229',
      footerBg: '#081410',
      // 文字色系
      text: '#e0ece6',
      textMuted: 'rgba(255, 255, 255, 0.5)',
      heading: '#ffffff',
      // 边框
      border: 'rgba(255, 255, 255, 0.08)',
      // 强调色系 —— 翡翠绿系
      primary: '#34d399',
      primaryDark: '#059669',
      accent: '#10b981',
      gradient: 'linear-gradient(135deg, #34d399, #059669)',
      // 导航
      navText: 'rgba(255, 255, 255, 0.7)',
      navHoverBg: 'rgba(52, 211, 153, 0.1)',
      // 交互效果
      cardBorderHover: 'rgba(52, 211, 153, 0.25)',
      cardShadow: '0 8px 30px rgba(0, 0, 0, 0.4)',
      headerShadow: '0 4px 30px rgba(0, 0, 0, 0.4)',
    },
  },

  'midnight': {
    name: { zh: '午夜黑', en: 'Midnight' },
    type: 'dark',
    colors: {
      // 背景色系 —— 极黑底，极致对比度
      bg: '#08090c',
      bgCard: '#111317',
      bgCardHover: '#1a1c22',
      footerBg: '#050608',
      // 文字色系 —— 最大可读性
      text: '#e8e8ed',
      textMuted: 'rgba(255, 255, 255, 0.5)',
      heading: '#ffffff',
      // 边框
      border: 'rgba(255, 255, 255, 0.1)',
      // 强调色系 —— 银灰冷调
      primary: '#c0c4d0',
      primaryDark: '#8b90a0',
      accent: '#a0a4b4',
      gradient: 'linear-gradient(135deg, #c0c4d0, #8b90a0)',
      // 导航
      navText: 'rgba(255, 255, 255, 0.65)',
      navHoverBg: 'rgba(192, 196, 208, 0.08)',
      // 交互效果
      cardBorderHover: 'rgba(192, 196, 208, 0.2)',
      cardShadow: '0 8px 30px rgba(0, 0, 0, 0.5)',
      headerShadow: '0 4px 30px rgba(0, 0, 0, 0.5)',
    },
  },

  'warm-gold': {
    name: { zh: '暖金色', en: 'Warm Gold' },
    type: 'dark',
    colors: {
      // 背景色系 —— 深棕金底，厚重工业质感
      bg: '#1a1408',
      bgCard: '#2d2410',
      bgCardHover: '#3d3418',
      footerBg: '#120e04',
      // 文字色系
      text: '#f0ead0',
      textMuted: 'rgba(255, 255, 255, 0.5)',
      heading: '#ffffff',
      // 边框
      border: 'rgba(255, 255, 255, 0.08)',
      // 强调色系 —— 琥珀金系
      primary: '#fbbf24',
      primaryDark: '#d97706',
      accent: '#f59e0b',
      gradient: 'linear-gradient(135deg, #fbbf24, #d97706)',
      // 导航
      navText: 'rgba(255, 255, 255, 0.7)',
      navHoverBg: 'rgba(251, 191, 36, 0.1)',
      // 交互效果
      cardBorderHover: 'rgba(251, 191, 36, 0.25)',
      cardShadow: '0 8px 30px rgba(0, 0, 0, 0.4)',
      headerShadow: '0 4px 30px rgba(0, 0, 0, 0.4)',
    },
  },
}

// ---------------------------------------------------------------------------
// 运行时主题管理
// ---------------------------------------------------------------------------

/** localStorage 存储 key */
const STORAGE_KEY = 'charmray-theme'

/**
 * 从 localStorage 读取用户上次选择的皮肤
 * @returns {string} 皮肤 key，默认 'tech-blue'
 */
function loadTheme() {
  try {
    return localStorage.getItem(STORAGE_KEY) || 'tech-blue'
  } catch {
    return 'tech-blue'
  }
}

/** 当前激活的皮肤 key */
const current = ref(loadTheme())

/**
 * 将皮肤配置应用到页面，设置所有 CSS 变量
 *
 * 按皮肤 type 自动处理深色/浅色的差异：
 *   - 深色系：文字为浅色，边框/遮罩使用 white alpha
 *   - 浅色系：文字为深色，边框/遮罩使用 black alpha
 *
 * @param {string} key - 皮肤 key
 */
function applyTheme(key) {
  const preset = presets[key]
  if (!preset) return

  const root = document.documentElement
  const c = preset.colors

  // ---- 背景色系 ----
  const bgRgb = hexToRgb(c.bg)
  root.style.setProperty('--color-bg', c.bg)
  root.style.setProperty('--color-bg-rgb', bgRgb)
  root.style.setProperty('--color-bg-card', c.bgCard)
  root.style.setProperty('--color-bg-card-hover', c.bgCardHover)
  root.style.setProperty('--color-footer-bg', c.footerBg)

  // ---- 文字色系 ----
  root.style.setProperty('--color-text', c.text)
  root.style.setProperty('--color-text-muted', c.textMuted)
  root.style.setProperty('--color-heading', c.heading)

  // ---- 边框 ----
  root.style.setProperty('--color-border', c.border)

  // ---- 强调色系 ----
  root.style.setProperty('--color-primary', c.primary)
  root.style.setProperty('--color-primary-dark', c.primaryDark)
  root.style.setProperty('--color-accent', c.accent)
  root.style.setProperty('--color-gradient', c.gradient)

  // ---- 导航 ----
  root.style.setProperty('--color-nav-text', c.navText)
  root.style.setProperty('--color-nav-hover-bg', c.navHoverBg)

  // ---- 交互效果 ----
  root.style.setProperty('--color-card-border-hover', c.cardBorderHover)
  root.style.setProperty('--color-card-shadow', c.cardShadow)
  root.style.setProperty('--color-header-shadow', c.headerShadow)
}

/**
 * 切换皮肤
 * @param {string} key - 目标皮肤 key
 */
function setTheme(key) {
  if (!presets[key]) return
  current.value = key
  try {
    localStorage.setItem(STORAGE_KEY, key)
  } catch {
    /* 隐私模式等场景下写入可能失败，忽略 */
  }
  applyTheme(key)
}

// ---- 页面加载时立即应用已保存的皮肤 ----
applyTheme(current.value)

// ---------------------------------------------------------------------------
// 导出
// ---------------------------------------------------------------------------

/**
 * Vue Composable: 皮肤管理
 *
 * 使用方式：
 *   import { useTheme } from '@/composables/useTheme'
 *   const { currentTheme, themeList, setTheme } = useTheme()
 *
 * @returns {{ currentTheme, themeList, setTheme }}
 *   - currentTheme: 当前皮肤 key 的响应式 ref
 *   - themeList: 所有可用皮肤的数组
 *   - setTheme: 切换皮肤的方法
 */
export function useTheme() {
  const themeList = Object.entries(presets).map(([key, val]) => ({
    key,
    ...val,
  }))

  return {
    currentTheme: current,
    themeList,
    setTheme,
  }
}
